import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ImagingOpenParams, PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { merge } from 'lodash-es';

import { ExtensaService } from '../../../connectivity/extensa.service';
import { IsIntegrationWebService } from '../../../connectivity/is-integration-web.service';
import {
    fromNg1OpenParams,
    fromNg1PacsStudyData,
    keyImageFromMessage,
    PacsStudyDataVnext,
    XmlRequestType,
} from '../common';
import { RootState } from '../../../root-store/root.state';
import { convertPropertiesToCamelCase } from '../../../utils/interop';
import {
    seViewerKeyImageAdd,
    seViewerKeyImageRefresh,
    seViewerKeyImagesDelete,
    seViewerKeyImagesSet,
    seViewerSRAdd,
    seViewerSRDelete,
    seViewerUSKineticMeasureImportRequest,
    seViewerUSMeasureDeleteRequest,
    seViewerUSMeasureDeleteResponse,
    seViewerUSMeasureGetReportRequest,
    seViewerUSMeasureGetReportResponse,
    seViewerUSMeasureImportRequestThirdParts,
    seViewerUSMeasureImportXmlRequest,
    seViewerUSStressEchoInit,
    seViewerUSStressEchoSelect,
} from '../../../root-store/root.actions';
import { DeepPartial, DeepPascalCase } from '../../../utils/types';
import { EstensaReviewConfirmationModalComponent } from './estensa-review-confirmation-modal.component';
import { AuthenticationService } from '../../authentication/authentication.service';
import { selectExtensaConnected } from '../../../connectivity/connectivity.selectors';
import { IViewerHelper } from '../angularjs';
import {
    DEFAULT_OPEN_IMAGES_OPTIONS,
    ExaminationStatusForImaging,
    ModelKeyImage,
    ModelKeyImageInstanceInfo,
    ModelShowImage,
    ModelStructuredReport,
    ModelUSMeasure,
    ModelUSMeasureShow,
    ModelUSStressEchoAssociateImages,
    ModelUSStressEchoInit,
    ModelUSStressEchoSelect,
    OpenImagesOptions,
    PacsTuple,
    RequestModelViewStudies,
} from '../ebit-viewer-api';
import { EstensaReviewApiService } from '../estensa-review/estensa-review-api.service';
import { Logger } from '@se/se.platform.base'; //WPF

//WPF
@Injectable({
    providedIn: 'root',
})
export class EstensaReviewViewerHelper implements IViewerHelper {
    readonly isVNext = true;
    readonly viewerType = 'EstensaReviewVNext';

    private isExtensaConnected$ = new BehaviorSubject<boolean>(false);
    private isOpeningViewer = false;

    constructor(
        private actions$: Actions,
        private authentication: AuthenticationService,
        private dialog: MatDialog,
        private estensaReviewApi: EstensaReviewApiService,
        private extensaService: ExtensaService,
        private isIntegration: IsIntegrationWebService,
        private logger: Logger,
        private store: Store<RootState>,
    ) {}

    async init() {
        if (this.authentication.isOpenedFromWpf) {
            return;
        }

        const hub = this.extensaService.signalrHubConnection.createHubProxy('ReviewHub');

        this.listenForViewerEvents(hub);

        this.listenForActions(hub);

        this.store.pipe(select(selectExtensaConnected)).subscribe(this.isExtensaConnected$);
    }

    /**
     * Apertura Review:
     * - Non fare nulla se Estensa è già aperta e l'utente è già loggato, o se il client è stato aperto da WPF.
     * - Nel caso estensa sia aperta, ma non l'utente è già loggato, fai solo login.
     * - Altrimenti, apri estensa, aspetta che sia disponibie per il login, e poi effettua il login.
     */
    async openViewer(forceIsIntegrationApp?: boolean) {
        if (this.isExtensaConnected$.value || this.isOpeningViewer || this.authentication.isOpenedFromWpf) {
            return;
        }

        this.isOpeningViewer = true;
        try {
            if (await this.extensaService.isAvailable()) {
                await this.extensaService.login();
                return;
            }
        } catch (err) {
            // Ignoriamo l'errore, potrebbe essere che la postazione non è abilitata all'imaging.
            this.logger.debug(err);
        } finally {
            this.isOpeningViewer = false;
        }

        this.isOpeningViewer = true;
        try {
            await this.startEstensa(forceIsIntegrationApp);
        } catch (err) {
            // Ignoriamo l'errore, potrebbe essere che la postazione non è abilitata all'imaging.
            this.logger.debug(err);
        } finally {
            this.isOpeningViewer = false;
        }
    }

    async isViewerOpen() {
        return this.extensaService.isAvailable();
    }

    async areImagesOpen() {
        return true;
    }

    async closeViewer() {
        this.logger.debug('closeviewer estensa review');
        if (this.authentication.hasSecondaryAuthentication) {
            return;
        }
        return this.estensaReviewApi.close({ force: true });
    }

    /**
     * Flusso di apertura:
     *   - Se richiesta l'apertura tramite isIntegrationApp (es. alla login), non usare l'apertura da url.
     *     In questo caso l'apertura può fallire senza problemi.
     *   - Altrimenti utilizza l'apertura tramite URL.
     *     Se fallisce (es. popup bloccati), prova con isIntegrationApp.
     */
    async startEstensa(forceIsIntegrationApp: boolean) {
        if (forceIsIntegrationApp || !this.openEstensaByUrl()) {
            await this.openEstensaByIsIntegration();
        }

        if (await this.extensaService.waitUntilAvailable()) {
            return await this.extensaService.login();
        }
        // waitUntilAvailable è fallita - probabilmente è andata in timeout. Se non abbiamo già provato, apri
        //   estensa con isIntegrationApp.
        if (!forceIsIntegrationApp) {
            const retry = await firstValueFrom(this.dialog.open(EstensaReviewConfirmationModalComponent).afterClosed());
            if (retry) {
                this.isOpeningViewer = false;
                return this.openViewer(true);
            }
        }
    }

    openEstensaByUrl() {
        return window.open('estensa://open');
    }

    async openEstensaByIsIntegration() {
        return this.isIntegration.startEstensa();
    }

    async discardImages(ask = false) {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.discard({ ask });
    }

    async openImages(studyData: PacsStudyDataVnext[], options: DeepPartial<OpenImagesOptions>) {
        if (!this.isReady()) {
            await this.openViewer();
        }

        const requestBody: RequestModelViewStudies = {
            ...merge({}, DEFAULT_OPEN_IMAGES_OPTIONS, options),
            studies: studyData.map((datum) => ({
                tags: {
                    patientID: datum.patient.patientId,
                    patientName: datum.patient.fullName,
                    patientSex: datum.patient.sex,
                    patientBirthDate: datum.patient.birthDate,
                    studyDate: datum.study.studyDate,
                    accessionNumber: datum.study.accessionNumber,
                    studyInstanceUID: datum.study.studyInstanceUID,
                    modality: datum.study.modality,
                },
            })),
        };
        return this.estensaReviewApi.viewImages(requestBody);
    }

    async setExaminationStatus(idExaminations: string[], examinationStatus: ExaminationStatusForImaging) {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.examinationStatus({ idExaminations, examinationStatus });
    }

    async deleteKeyImage(data: ModelKeyImage): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.deleteKeyImage(data);
    }

    async usStressEchoSelectStage(stage: string): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.usStressEchoSelectStage(stage);
    }

    async usStressEchoSelectView(view: string): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.usStressEchoSelectView(view);
    }

    async uSStressEchoAssociateImages(model: ModelUSStressEchoAssociateImages): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.usStressEchoAssociateImages(model);
    }

    async showImage(model: ModelShowImage): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.showImage(model);
    }

    async usMeasureShow(model: ModelUSMeasureShow): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return this.estensaReviewApi.usMeasureShow(model);
    }

    openImagesRequired(imagingData: PacsStudyDataNg1[], options?: DeepPartial<ImagingOpenParams>) {
        this.openImages(imagingData.map(fromNg1PacsStudyData), fromNg1OpenParams(options ?? {}));
    }

    discardImagesRequired() {
        this.discardImages();
    }

    closeViewerRequired() {
        this.closeViewer();
    }

    isFormSubmitting() {
        return false;
    }

    private isReady() {
        return this.isExtensaConnected$.value && !this.authentication.isOpenedFromWpf;
    }

    private listenForViewerEvents(hub: SignalR.Hub.Proxy) {
        this.estensaReviewApi
            .on(hub, 'studyInstanceUID', () => this.onUnimplemented('studyInstanceUID'))
            // Immagini chiave
            .on(hub, 'addKeyImage', (msg) => this.onAddKeyImage(msg))
            .on(hub, 'deleteKeyImage', (msg) => this.onDeleteKeyImage(msg))
            .on(hub, 'refreshKeyImage', (msg) => this.onRefreshKeyImage(msg))
            .on(hub, 'keyImagesInfo', (msg) => this.onSetKeyImages(msg))
            // Misure US
            .on(hub, 'usMeasureDeleteSourceAndImportToReport', (measure, callback, requestId) =>
                this.onUsMeasureDelete(measure, callback, requestId),
            )
            .on(hub, 'usMeasureGetReportInfo', (npla, callback, requestId) =>
                this.onUsMeasureGetReportInfo(npla, callback, requestId),
            )
            .on(hub, 'usMeasureImportToReport', (msg) => this.onUsMeasureImport(msg))
            // Oggetti Structured Report
            .on(hub, 'addStructuredReport', (msg) => this.onSrAdd(msg))
            .on(hub, 'deleteStructuredReport', (msg) => this.onSrDelete(msg))
            // Oggetti Key Object Selection
            .on(hub, 'addKeyObjectSelection', () => this.onUnimplemented('addKeyObjectSelection'))
            .on(hub, 'deleteKeyObjectSelection', () => this.onUnimplemented('addKeyObjectSelection'))
            // Risultati XML da applicativi esterni
            .on(hub, 'xmlCaasData', (msg) => this.onXmlImport(XmlRequestType.Caas)(msg?.toString()))
            .on(hub, 'xmlCaasMrData', (msg) => this.onXmlImport(XmlRequestType.Caas)(msg?.toString()))
            .on(hub, 'xmlTeraReconData', (msg) => this.onXmlImport(XmlRequestType.TeraRecon)(msg?.toString()))
            // Stress Echo
            .on(hub, 'usStressEchoInit', (msg) => this.onUsStressEchoInit(msg))
            .on(hub, 'usStressEchoSelect', (msg) => this.onUsStressEchoSelect(msg))
            // Left Ventricle Strain
            .on(hub, 'lvStrainStart', () => this.onUnimplemented('lvStrainStart'))
            .on(hub, 'lvStrainEnd', () => this.onUnimplemented('lvStrainEnd'))
            .on(hub, 'lvStrainResultClear', () => this.onUnimplemented('lvStrainResult'))
            .on(hub, 'lvStrainResult', (msg) => this.onXmlImport(XmlRequestType.DiaImagingLVStrain)(msg))
            // Right Ventricle Strain
            .on(hub, 'rvStrainStart', () => this.onUnimplemented('rvStrainStart'))
            .on(hub, 'rvStrainEnd', () => this.onUnimplemented('rvStrainEnd'))
            .on(hub, 'rvStrainResultClear', () => this.onUnimplemented('rvStrainResultClear'))
            .on(hub, 'rvStrainResult', (msg) => this.onXmlImport(XmlRequestType.DiaImagingRVStrain)(msg))
            // Left ventricle Wall Motion
            .on(hub, 'lvWallMotionStart', () => this.onUnimplemented('lvWallMotionStart'))
            .on(hub, 'lvWallMotionEnd', () => this.onUnimplemented('lvWallMotionEnd'))
            .on(hub, 'lvWallMotionResultClear', () => this.onUnimplemented('lvWallMotionResultClear'))
            .on(hub, 'lvWallMotionResult', (msg) => this.onXmlImport(XmlRequestType.DiaImagingLVWallMotion)(msg))
            // Misure generiche
            .on(hub, 'addGroupMeasure', () => this.onUnimplemented('addGroupMeasure'))
            .on(hub, 'deleteMeasure', () => this.onUnimplemented('deleteMeasure'))
            .on(hub, 'modifyMeasure', () => this.onUnimplemented('modifyMeasure'));
    }

    //#region Viewer callbacks

    private listenForActions(hub: SignalR.Hub.Proxy) {
        this.actions$.pipe(ofType(seViewerUSMeasureDeleteResponse)).subscribe((action) => {
            hub.invoke(action.callback, action.requestId, action.result);
        });
        this.actions$.pipe(ofType(seViewerUSMeasureGetReportResponse)).subscribe((action) => {
            hub.invoke(action.callback, action.requestId, action.report);
        });
    }

    private onAddKeyImage(msg: DeepPascalCase<ModelKeyImage>) {
        this.store.dispatch(
            seViewerKeyImageAdd({
                image: keyImageFromMessage(msg),
                pacsTuple: convertPropertiesToCamelCase(msg.NPla),
            }),
        );
    }

    private onDeleteKeyImage(msg: DeepPascalCase<ModelKeyImage>) {
        this.store.dispatch(
            seViewerKeyImagesDelete({
                images: [{ image: keyImageFromMessage(msg), pacsTuple: convertPropertiesToCamelCase(msg.NPla) }],
            }),
        );
    }

    private onRefreshKeyImage(msg: DeepPascalCase<ModelKeyImage>) {
        this.store.dispatch(
            seViewerKeyImageRefresh({
                image: keyImageFromMessage(msg),
                pacsTuple: convertPropertiesToCamelCase(msg.NPla),
            }),
        );
    }

    private onSetKeyImages(msg: DeepPascalCase<ModelKeyImageInstanceInfo[]>) {
        this.store.dispatch(
            seViewerKeyImagesSet({
                images: msg.map((m) => ({
                    image: keyImageFromMessage(m),
                    pacsTuple: convertPropertiesToCamelCase(m.NPla),
                })),
            }),
        );
    }

    private onSrDelete(msg: DeepPascalCase<ModelStructuredReport>) {
        this.store.dispatch(seViewerSRDelete(convertPropertiesToCamelCase(msg)));
    }

    private onSrAdd(msg: DeepPascalCase<ModelStructuredReport>) {
        this.store.dispatch(seViewerSRAdd(convertPropertiesToCamelCase(msg)));
    }

    private onUsMeasureDelete(measure: DeepPascalCase<ModelUSMeasure>, callback: string, requestId: string) {
        this.store.dispatch(
            seViewerUSMeasureDeleteRequest({
                measure: convertPropertiesToCamelCase(measure),
                callback,
                requestId,
            }),
        );
    }

    private onUsMeasureGetReportInfo(npla: DeepPascalCase<PacsTuple>, callback: string, requestId: string) {
        this.store.dispatch(
            seViewerUSMeasureGetReportRequest({
                pacsTuple: convertPropertiesToCamelCase(npla),
                callback,
                requestId,
            }),
        );
    }

    private onUsMeasureImport(msg: DeepPascalCase<ModelUSMeasure>) {
        this.store.dispatch(seViewerUSMeasureImportXmlRequest(convertPropertiesToCamelCase(msg)));
    }

    private onUsStressEchoInit(msg: DeepPascalCase<ModelUSStressEchoInit>) {
        this.store.dispatch(seViewerUSStressEchoInit(convertPropertiesToCamelCase(msg)));
    }

    private onUsStressEchoSelect(msg: DeepPascalCase<ModelUSStressEchoSelect>) {
        this.store.dispatch(seViewerUSStressEchoSelect(convertPropertiesToCamelCase(msg)));
    }

    private onXmlImport(requestType: XmlRequestType) {
        return (msg: string) => {
            switch (requestType) {
                case XmlRequestType.DiaImagingLVStrain:
                case XmlRequestType.DiaImagingRVStrain:
                case XmlRequestType.DiaImagingLVWallMotion:
                    return this.store.dispatch(seViewerUSKineticMeasureImportRequest({ requestType, xml: msg }));
                case XmlRequestType.Caas:
                default:
                    this.store.dispatch(seViewerUSMeasureImportRequestThirdParts({ requestType, xml: msg }));
            }
        };
    }

    private onUnimplemented(messageName: string) {
        return () => {
            this.logger.debug(`Unimplemented viewer message: ${messageName}`);
        };
    }

    //#endregion
}
